<template>
  <bm-card v-bind="$attrs" @header-click="toggleExpanded">
    <template #prepend>
      <bm-icon
        icon="KeyboardArrowDown"
        :class="{
          'expand-icon-active': isExpanded,
          'mr-3': true,
          'no-print': true,
        }"
      />
    </template>

    <!-- Passes all given slots to BmCard, adds the "isExpanded" logic to prevent visibility on collapse -->
    <template v-for="(_, slot) of $slots" #[slot]>
      <!-- Expansion looks more natural on only the main content, gets jolty otherwise -->
      <template v-if="slot === 'default'">
        <v-expand-transition :key="slot">
          <slot v-if="isExpanded" :name="slot" />
        </v-expand-transition>
      </template>
      <template v-else-if="slot === 'prepend-label' || slot === 'menu'">
        <slot :name="slot" />
      </template>
      <template v-else>
        <slot v-if="isExpanded" :name="slot" />
      </template>
    </template>
  </bm-card>
</template>

<script setup lang="ts">
import BmCard from "@/components/controls/card/BmCard.vue";
import { watch } from "vue";
import type { BmUiState } from "@/components/controls/input/state/BmUIStateRepository";
import { BmUiStateRepository } from "@/components/controls/input/state/BmUIStateRepository";
import { isNullish } from "@/app/apiutils";
import { makeRef } from "@/components/composables/reactivityTypes";
import type { Maybe } from "@/components/utils/typeHelpers";

interface BmCollapsibleCardProps {
  defaultExpanded?: Maybe<boolean>;
  uiStateKey?: string;
}

const props = defineProps<BmCollapsibleCardProps>();

interface BmCollapsibleCardEvents {
  (eventName: "toggle-expanded", isExpanded: boolean): void;
}
const emit = defineEmits<BmCollapsibleCardEvents>();

interface BmCollapsibleCardUiState extends BmUiState {
  isExpanded: boolean;
}

const isExpanded = makeRef(props.defaultExpanded ?? true);

if (props.uiStateKey) {
  const savedExpandedState = BmUiStateRepository.load<BmCollapsibleCardUiState>(
    props.uiStateKey
  ).isExpanded;

  if (!isNullish(savedExpandedState)) {
    if (
      !isNullish(props.defaultExpanded) &&
      savedExpandedState !== props.defaultExpanded
    ) {
      emit("toggle-expanded", savedExpandedState);
    }
    isExpanded.value = savedExpandedState;
  }
}

const saveUiState = () => {
  if (props.uiStateKey) {
    BmUiStateRepository.save<BmCollapsibleCardUiState>(props.uiStateKey, {
      isExpanded: isExpanded.value,
    });
  }
};

watch(
  () => props.defaultExpanded,
  () => {
    if (!isNullish(props.defaultExpanded)) {
      isExpanded.value = props.defaultExpanded;
      saveUiState();
    }
  }
);

const toggleExpanded = () => {
  isExpanded.value = !isExpanded.value;
  emit("toggle-expanded", isExpanded.value);
  saveUiState();
};
</script>

<style scoped lang="sass">
@import "src/sass/colours"

.expand-icon-active
  transform: rotate(-180deg)

// Remove bottom margin and move to text for consistent transition
:deep(.bm-card-title)
  cursor: pointer
  user-select: none
  margin-bottom: 0

// Remove margin and padding so an empty block isnt rendered
// Transition is needed, gives a soft-close effect and prevents jolting
:deep(.v-card__text:empty)
  transition: margin 0.15s, padding 0.15s
  padding: 0
  margin: 0

// We removed the margin from the card title, so add it back to the card body to keep the style
// We're using padding here since margins don't seem to animate as nicely
:deep(.v-card__text)
  padding-top: 10px
</style>
